import { useNavigate } from 'react-router-dom';

/**
 * Standard back affordance used across the PWA. A circular icon-only button
 * matching the marketing site's coach profile and the iOS convention — the
 * word "Back" is gone; the chevron and position carry the meaning.
 *
 * Navigation:
 * - When `onClick` is supplied, it takes precedence. Use this for in-panel
 *   or in-popover state transitions where browser-level history doesn't
 *   reflect the user's last navigation (Lessons / Orders / LessonOrderPanel
 *   internal nav stacks, PendingUploadsIndicator popover, etc.).
 * - Otherwise: if there's prior in-app history (React Router tracks an
 *   `idx` on `history.state` — `> 0` means at least one entry was pushed
 *   in this session), pop it via `navigate(-1)`. If not (direct deep
 *   link, refresh, or first SPA entry), fall back to `fallback` (default
 *   `/lessons`) so the button never strands the user on a blank route.
 *
 * `variant="inverse"` survives because three callers render this on a dark
 * surface — the coach-profile hero, MyProfile's navy strip, and the program
 * conversation bar — where a light grey circle would disappear. It keeps the
 * same geometry and swaps to a translucent white fill.
 */
interface BackButtonProps {
  onClick?: () => void;
  fallback?: string;
  /**
   * @deprecated No longer affects rendering — every back button is one 36px
   * circle now. Kept so the ~18 existing call sites compile unchanged.
   */
  size?: 'sm' | 'md';
  variant?: 'default' | 'inverse';
  /**
   * Omit the default bottom margin. Use inside fixed top bars, header
   * strips, panel headers, or absolute-positioned hero overlays — any
   * container that handles its own spacing. Callers that want a *different*
   * bottom margin (e.g., `mb-6`, `mb-2`) should set `flush` and supply
   * their own `mb-*` via `className` to avoid Tailwind's same-property
   * conflict ambiguity.
   */
  flush?: boolean;
  /**
   * Lift the button out of page flow: fixed at top 12 / left 16, translucent
   * with a backdrop blur, z-40 so it stays under the mobile bottom nav. For
   * full pages only — never inside a header bar or a two-pane detail panel,
   * where fixed would tear it out of its container. Implies `flush`.
   */
  floating?: boolean;
  /**
   * Which wash a floating button uses. `dark` (default) is for dark surfaces
   * and images; `light` is for light page backgrounds, where the dark wash
   * would read as a grey smudge.
   */
  tone?: 'dark' | 'light';
  /**
   * Distance from the top of the viewport for a `floating` button. Defaults to
   * 12, which is right on a page with no bar above it. Desktop pages inside
   * Layout have a 72-high sticky top bar at z-50, and this button sits at
   * z-40 — so on those, 12 puts it behind the bar. Pass 84 to clear it.
   * A string takes any CSS length, which Inbox uses to follow the visual
   * viewport while the keyboard is open.
   */
  floatingTop?: number | string;
  /**
   * Distance from the left of the viewport for a `floating` button. Defaults to
   * 16, the page edge. Pass 48 to start it at the top bar logo's left edge,
   * as `gutter` does; see AccountPage.
   */
  floatingLeft?: number;
  /**
   * Keeps the button in flow but pinned as the page scrolls. For a full-page
   * detail screen whose back control would otherwise scroll out of reach.
   *
   * Depends on Layout's <main> using `overflow-x: clip` rather than `hidden`:
   * `hidden` makes <main> a scroll container that never scrolls, against which
   * a sticky child is inert. Not for a button inside a two-pane panel, where
   * the panel scrolls and the page does not.
   */
  sticky?: boolean;
  /**
   * A page's own back circle, at the top of its content column. From 1280
   * wide it leaves the column for the page's left gutter: fixed, its left
   * edge on the top bar logo's, under the bar (`.back-gutter` in index.css).
   * Narrower, it stays where it is. Not for a panel or a header strip.
   */
  gutter?: boolean;
  /**
   * @deprecated The button is always icon-only now. Kept for the one caller
   * that still passes it.
   */
  iconOnly?: boolean;
  /**
   * Names where back goes ("Lesson"), shown after the chevron in a capsule.
   * Optional and additive: without it the button is the 36 circle every
   * other caller gets, unchanged. Used by the phone clip recorder, whose
   * only exit this is (docs/mobile-editor-layout-spec.md 10.4).
   */
  label?: string;
  className?: string;
}

export default function BackButton({
  onClick,
  fallback = '/lessons',
  variant = 'default',
  flush = false,
  floating = false,
  tone = 'dark',
  floatingTop = 12,
  floatingLeft = 16,
  sticky = false,
  gutter = false,
  label,
  className = '',
}: BackButtonProps) {
  const navigate = useNavigate();

  const handleClick = () => {
    if (onClick) { onClick(); return; }
    const idx = (window.history.state as { idx?: number } | null)?.idx;
    if (typeof idx === 'number' && idx > 0) {
      navigate(-1);
    } else {
      navigate(fallback, { replace: true });
    }
  };

  const stickyStyle: React.CSSProperties | undefined = sticky
    ? { position: 'sticky', top: 12, zIndex: 30 }
    : undefined;

  const isInverse = variant === 'inverse';
  const bottomMargin = floating || flush ? '' : 'mb-5';

  // Floating: fixed to the viewport, translucent, out of page flow. z-40 stays
  // under the mobile bottom nav, which index.css pins at 9999.
  //
  // `tone` exists because a translucent fill only works over something. Over a
  // dark surface or an image the dark wash reads; over the light page
  // background it would be a grey smudge, so that case inverts to a white wash
  // with a hairline border.
  const floatingStyle: React.CSSProperties | undefined = floating
    ? {
        position: 'fixed',
        top: floatingTop,
        left: floatingLeft,
        zIndex: 40,
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
        ...(tone === 'dark'
          ? { background: 'rgba(0,0,0,0.16)', border: 'none' }
          : { background: 'rgba(255,255,255,0.72)', border: '1px solid rgba(0,0,0,0.06)' }),
      }
    : undefined;

  const floatingChevron = floating ? (tone === 'dark' ? '#FFFFFF' : '#1C1C1C') : null;

  return (
    // The circle stays 36px; a transparent ::after at -4px on every side takes
    // the tap target to 44px without touching layout or anything visible.
    <button
      type="button"
      onClick={handleClick}
      aria-label={label ? `Back to ${label}` : 'Back'}
      className={`relative after:absolute after:-inset-1 after:content-[''] inline-flex items-center justify-center ${label ? 'gap-0.5 pl-1.5 pr-3' : 'w-9'} h-9 rounded-full border-none cursor-pointer transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-db-accent/40 ${bottomMargin} ${gutter ? 'back-gutter' : ''} ${className}`}
      style={floatingStyle ?? {
        background: isInverse ? 'rgba(255,255,255,0.14)' : '#F0F0EE',
        ...stickyStyle,
      }}
      onMouseEnter={(e) => {
        if (floating) return;
        e.currentTarget.style.background = isInverse ? 'rgba(255,255,255,0.24)' : '#E4E4E2';
      }}
      onMouseLeave={(e) => {
        if (floating) return;
        e.currentTarget.style.background = isInverse ? 'rgba(255,255,255,0.14)' : '#F0F0EE';
      }}
    >
      {/* marginLeft -1: the chevron's mass sits right of its bounding box, so a
          mathematically centred glyph reads as pushed right. iOS applies the
          same 1px nudge. */}
      <svg
        width="20" height="20" fill="none" viewBox="0 0 24 24"
        stroke={floatingChevron ?? (isInverse ? '#FFFFFF' : '#1C1C1C')}
        strokeWidth="2.75" strokeLinecap="round" strokeLinejoin="round"
        style={{ marginLeft: -1 }}
        aria-hidden="true"
      >
        <polyline points="15 18 9 12 15 6" />
      </svg>
      {/* Body 17/22, the label colour of the chevron. */}
      {label && (
        <span className="text-[17px] leading-[22px] font-medium" style={{ color: floatingChevron ?? (isInverse ? '#FFFFFF' : '#1C1C1C') }}>
          {label}
        </span>
      )}
    </button>
  );
}
